import { useCallback, useEffect, useState } from 'react';
import type { OrgPartVariant, UpsertOrgPartVariantInput } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Variantes tunées d'organisation (ADR-0030 §3) : versions « réglées » d'une pièce officielle,
 * **cloisonnées** et **toujours rattachées** à un officiel (`parentPartId`). Lecture de toutes les
 * variantes de l'organisation + création. `create` propage son erreur (403 pour un non-éditeur, 404
 * si l'officiel parent est inconnu) ; la lecture tombe en liste vide sans bloquer.
 */
export function usePartVariants() {
  const [variants, setVariants] = useState<OrgPartVariant[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setVariants(await apiFetch<OrgPartVariant[]>('/part-variants'));
    } catch {
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (input: UpsertOrgPartVariantInput): Promise<OrgPartVariant> => {
      const created = await apiFetch<OrgPartVariant>('/part-variants', {
        method: 'POST',
        body: input,
      });
      await refresh();
      return created;
    },
    [refresh],
  );

  return { variants, loading, create };
}
